<template>
  <div class="collaborator-list-wrp">
    <div v-if="loadingCollaborators" class="flex justify-center">
      <aha-spinner class="py-5" />
    </div>
    <ul
      v-else
      class="collaborator-list"
      :class="{'share-v2': isTreatmentShareV2}"
      @scroll="onScrollCollaborateList"
    >
      <li
        v-for="(collaborator, index) in collaborators"
        :key="collaborator.email"
        class="collaborator-item"
      >
        <div class="flex flex-row items-center">
          <aha-avatar
            class="mr-2"
            :name="getCollaboratorName(collaborator)"
            :image="collaborator.avatar"
            :width="36"
            :height="36"
            :id="collaborator.id"
          />
          <div class="collaborator-info">
            <p class="collaborator-info__name">
              {{ getCollaboratorName(collaborator) }}
            </p>
            <p class="collaborator-info__email">
              {{ collaborator.email }}
            </p>
          </div>
          <div class="collaborator-role" :id="`collaborator-${collaborator.email}`">
            <span v-if="collaborator.role === 'owner'">
              {{ $i18nPresenter.t('Owner') }}
            </span>
            <span
              v-else
              @click="toggleCollaboratorDropdown(collaborator.email)"
              class="collaborator-role--dropdown-arrow"
            >
              {{ $i18nPresenter.t('Can edit') }}
              <aha-icon
                name="arrow"
                height="12"
                width="12"
                viewBox="0 0 24 24"
              />
            </span>
            <aha-context-menu
              :classList="'force-z-index'"
              :ref="`collaborator-menu-${collaborator.email}`"
              :target="`collaborator-${collaborator.email}`"
              :forceMenuPosition="true"
              :x="0"
              :y="getDropdownY(index)"
              @hide="onHideDropdown"
            >
              <aha-context-menu-item :clickable="false">
                <aha-icon
                  name="check"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  class="mr-1 text-primary-90"
                />
                {{ $i18nPresenter.t('Can edit') }}
              </aha-context-menu-item>
              <div class="border-b border-solid border-base-30" @click.stop />
              <aha-context-menu-item
                :disabled="canNotRemoveCollaborator(collaborator)"
                @click="removeCollaborator(collaborator)"
              >
                <aha-icon
                  name="check"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  class="mr-1 text-primary-90"
                  style="visibility: hidden"
                />
                <span style="margin-left: 0" v-if="collaborator.email !== currentUser.email">
                  {{ $i18nPresenter.t('Remove') }}
                </span>
                <span style="margin-left: 0" v-if="collaborator.email === currentUser.email">
                  {{ $i18nPresenter.t('Leave') }}
                </span>
              </aha-context-menu-item>
            </aha-context-menu>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'
import EmailUtils from '@/utils/email'

export default {
  props: {
    presentation: {
      type: Object,
      default: () => {},
    },
    loadingCollaborators: {
      type: Boolean,
      default: false,
    },
    collaborators: {
      type: Array,
      default: () => [],
    },
    isDisableEditPresentation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      targetX: null,
      targetY: null,
      scrollTop: 0,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters(['isTreatmentShareV2']),
  },

  methods: {
    onScrollCollaborateList: _.throttle(function checkScrollTop(e) {
      this.scrollTop = e.srcElement.scrollTop
    }, 500),
    getDropdownY(index) {
      const listHeight = this.collaborators.length >= 4 ? 240 : this.collaborators.length * 60
      const dropdownHeight = 80
      const spaceBetweenItems = 70
      return (listHeight - index * spaceBetweenItems + this.scrollTop < dropdownHeight) ? -90 : 5
    },
    getCollaboratorName(collaborator) {
      if (collaborator.id && collaborator.firstName) {
        return `${collaborator.firstName} ${collaborator.lastName}`
      }
      if (collaborator.email) {
        return EmailUtils.getEmailName(collaborator.email)
      }
      return 'Unknown user'
    },
    toggleCollaboratorDropdown(email) {
      // Fix AHA-5280: The modal content height is automatically increasing on Firefox
      // So we need to fix a height here
      const elem = document.querySelector('.aha-modal__content')
      if (elem) {
        elem.style.height = `${elem.scrollHeight}px`
      }
      // Open the dropdown menu
      this.$refs[`collaborator-menu-${email}`][0].show()
    },

    onHideDropdown() {
      const elem = document.querySelector('.aha-modal__content')
      if (elem) {
        elem.style.height = null
      }
    },

    canNotRemoveCollaborator(collaborator) {
      // Can remove non-user
      if (!collaborator.id) return false
      // Can remove myself (leave collaborating)
      if (collaborator.id === this.currentUser.id) return false
      // Cannot remove current editor, but can remove the others
      return (this.presentation.takenOverBy === collaborator.id)
    },

    removeCollaborator(collaborator) {
      this.$emit('removeCollaborator', collaborator)
    },
  },
}
</script>

<style lang="scss" scoped>
.collaborator-list-wrp {
  ::v-deep .context-menu-wrapper {
    min-width: 6rem;
    padding: 0;
  }
}

.collaborator-list {
  @apply pr-2 max-h-60 overflow-y-auto;
  &.share-v2{
    max-height: 370px;
  }
}

.collaborator-item {
  @apply py-3;
}

.collaborator-info {
  width: 70%;
}

.collaborator-info__name {
  @apply font-bold;
}
.collaborator-info__email {
  @apply text-base-80 text-xs;
}

.collaborator-role {
  @apply ml-auto text-base-90 text-xs text-right relative;
  min-width: 60px;
}
.collaborator-role--dropdown {
  @apply bg-base-0 absolute top-5 right-0;
  @apply border border-base-30;
  @apply rounded w-24 z-30;
  box-shadow: 3px 3px 5px rgba(211, 215, 225, 0.42);
}
.collaborator-role--dropdown-item {
  @apply p-2;
  &.clickable {
    &:hover {
      @apply text-red-100;
    }
    @apply cursor-pointer;
  }
}
.collaborator-role--dropdown-arrow {
  @apply cursor-pointer z-20;
}

</style>
